// src/views/reports/OverdueJobsReportView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, onSnapshot, orderBy, doc } from 'firebase/firestore';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Download, Clock, ExternalLink } from 'lucide-react'; 
import { format } from 'date-fns';
import { exportToCSV } from '../../utils/csvExport';
import JobFormModal from '../../components/crm/JobFormModal'; // Import the Editor

const OverdueJobsReportView = ({ currentUserProfile }) => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // --- STATE FOR MODAL SUPPORT ---
    const [employees, setEmployees] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [resources, setResources] = useState([]);
    const [companySettings, setCompanySettings] = useState(null); // orgtime-7c2e4b19 — the job form needs the company's zone (and its other settings)
    const [selectedJob, setSelectedJob] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // --- DATA FETCHING ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        // 1. Fetch Jobs
        const qJobs = query(collection(db, `organizations/${orgId}/jobs`), orderBy('endAt', 'asc'));
        const unsubJobs = onSnapshot(qJobs, (snapshot) => {
            setJobs(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); // s6costing-docid: doc id wins over a stale stored `id` field
            setLoading(false);
        });

        // 2. Fetch Helpers (Required for the Modal to work)
        const unsubEmp = onSnapshot(collection(db, `organizations/${orgId}/employees`), (snap) => 
            setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        const unsubCust = onSnapshot(collection(db, `organizations/${orgId}/customers`), (snap) => 
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        const unsubRes = onSnapshot(collection(db, `organizations/${orgId}/resources`), (snap) => 
            setResources(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubOrg = onSnapshot(doc(db, 'organizations', orgId), (snap) => setCompanySettings(snap.exists() ? snap.data() : null)); // orgtime-7c2e4b19

        return () => {
            unsubOrg();
            unsubJobs();
            unsubEmp();
            unsubCust();
            unsubRes();
        };
    }, [currentUserProfile]);

    // --- FILTER LOGIC ---
    const overdueJobs = useMemo(() => {
        const now = new Date();
        return jobs.filter(job => {
            if (job.eventType !== 'Job') return false; 
            if (job.status === 'Completed' || job.status === 'Canceled') return false;
            const endDate = job.endAt?.toDate ? job.endAt.toDate() : new Date(job.endAt);
            return endDate < now;
        });
    }, [jobs]);

    // --- HANDLERS ---
    const handleRowClick = (job) => {
        setSelectedJob(job);
        setIsModalOpen(true);
    };

    const handleExport = () => {
        if (!overdueJobs.length) return alert("No data to export");
        const csvData = overdueJobs.map(job => ({
            Due_Date: job.endAt?.toDate ? format(job.endAt.toDate(), 'yyyy-MM-dd HH:mm') : 'N/A',
            Customer: job.clientName || 'Unknown',
            Job_Title: job.title,
            Status: job.status,
            Assigned_To: job.assignedToNames ? job.assignedToNames.join(', ') : 'Unassigned',
        }));
        exportToCSV(csvData, `Overdue_Jobs_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    };

    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---
    
    if (loading) return <div className="p-10 text-center text-gray-500">Scanning jobs...</div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            Overdue Jobs
                            <span className="text-sm font-normal bg-red-100 text-red-800 px-2 py-0.5 rounded-full border border-red-200">
                                {overdueJobs.length} Found
                            </span>
                        </h1>
                        <p className="text-gray-500 text-sm">Click any row to resolve the job</p>
                    </div>
                </div>
                <button 
                    onClick={handleExport}
                    className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <Download size={18} /> Export CSV
                </button>
            </div>

            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-red-50 text-red-800 text-sm uppercase border-b border-red-100">
                            <th className="p-4">Due Date</th>
                            <th className="p-4">Customer</th>
                            <th className="p-4">Job Title</th>
                            <th className="p-4">Assigned To</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Action</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-gray-700">
                        {overdueJobs.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="p-12 text-center text-green-600">
                                    <div className="flex flex-col items-center gap-2">
                                        <Clock size={48} className="text-green-200" />
                                        <span className="font-medium">All jobs are on schedule!</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            overdueJobs.map(job => (
                                <tr 
                                    key={job.id} 
                                    onClick={() => handleRowClick(job)}
                                    className="border-b hover:bg-blue-50 cursor-pointer transition-colors group"
                                >
                                    <td className="p-4 font-bold text-red-600">
                                        {job.endAt?.toDate ? format(job.endAt.toDate(), 'MMM dd, HH:mm') : 'N/A'}
                                    </td>
                                    <td className="p-4">{job.clientName}</td>
                                    <td className="p-4 font-medium">{job.title}</td>
                                    <td className="p-4 text-gray-500">
                                        {job.assignedToNames?.length > 0 ? job.assignedToNames.join(', ') : <span className="text-red-400 italic">Unassigned</span>}
                                    </td>
                                    <td className="p-4">
                                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-white border border-gray-300 text-gray-600">
                                            {job.status}
                                        </span>
                                    </td>
                                    <td className="p-4 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="flex items-center gap-1 text-xs font-bold uppercase">
                                            Open <ExternalLink size={12} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- JOB EDITOR MODAL (Pop-up) --- */}
            {isModalOpen && (
                <JobFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    currentUserProfile={currentUserProfile}
                    jobToEdit={selectedJob}
                    employees={employees}
                    customers={customers}
                    resources={resources}
                    companySettings={companySettings}
                    jobs={jobs} // Passed for conflict detection
                    onSave={() => {
                        setIsModalOpen(false);
                        // The onSnapshot listener will automatically refresh the list
                    }}
                />
            )}
        </div>
    );
};

export default OverdueJobsReportView;